import {
  Box,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import {
  FieldWrapperVertical,
  ReadOnly,
  useEntityPermission,
  useFormPermissions,
  useLocalization,
} from '@ui-framework/index';
import { getWeekdays } from '@product-framework/index';
import { SelectStatic } from '@product-framework/components/FormFields';
import { forwardRef, useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import {
  FIRST_LAST_WEEK_OF_MONTH_OPTIONS,
  WEEK_OF_MONTH_OPTIONS,
} from '../../../../utils/constants';

const RecurrenceOptionsPatternMonthly = forwardRef((props, ref) => {
  const { formatMessage } = useLocalization();
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
    clearErrors,
  } = useFormContext();
  const { permissions } = useFormPermissions();
  const { isReadOnly } = useEntityPermission();
  const recurrenceMonthlyMode = useWatch({
    control,
    name: 'recurrenceMonthlyMode',
  });

  const WEEKDAYS_LONG = getWeekdays(formatMessage).map(wk => ({
    label: wk.longLabel,
    value: wk.value,
  }));

  const currentValues = getValues();

  const dayArray = Array.from({ length: 31 }, (_, i) => ({
    label: i + 1,
    value: i + 1,
  }));

  useEffect(() => {
    if (!recurrenceMonthlyMode) {
      setValue('recurrenceMonthlyMode', 'DAY_OF_MONTH');
    }
    clearErrors('recurrenceMonthlyMode');
  }, [recurrenceMonthlyMode]);

  if (isReadOnly(permissions?.recurrenceMonthlyMode, recurrenceMonthlyMode)) {
    if (recurrenceMonthlyMode === 'DAY_OF_MONTH') {
      return (
        <ReadOnly>
          <FormattedMessage
            id="DAY_DAY_OF_THE_MONTH"
            defaultMessage="Day {day} of the month"
            values={{ day: currentValues?.recurrenceMonthlyMode1DOM }}
          />
        </ReadOnly>
      );
    }
    if (recurrenceMonthlyMode === 'ORDINAL_WEEKDAY') {
      return (
        <ReadOnly>
          <FormattedMessage
            id="THE_ORDINAL_WEEKDAY_OF_THE_MONTH"
            defaultMessage="The {ordinal} {weekday} of the month"
            values={{
              ordinal: WEEK_OF_MONTH_OPTIONS(formatMessage).find(
                m => m.value === currentValues?.recurrenceMonthlyMode2OWO,
              ).label,
              weekday: WEEKDAYS_LONG.find(
                m => m.value === currentValues?.recurrenceMonthlyMode2OWW,
              ).label,
            }}
          />
        </ReadOnly>
      );
    }
    if (recurrenceMonthlyMode === 'ORDINAL_DAY') {
      return (
        <ReadOnly>
          <FormattedMessage
            id="ORDINAL_DAY_OF_THE_MONTH"
            defaultMessage="{ordinal} day of the month"
            values={{
              ordinal: FIRST_LAST_WEEK_OF_MONTH_OPTIONS(formatMessage).find(
                m => m.value === currentValues?.recurrenceMonthlyMode3O,
              ).label,
            }}
          />
        </ReadOnly>
      );
    }
  }

  return (
    <Stack spacing={2} ref={ref}>
      <FieldWrapperVertical
        field={{
          name: 'recurrenceMonthlyMode',
          label: {
            localeId: 'MONTHLY_OPTIONS',
            defaultMessage: 'Monthly Options',
          },
          required: true,
        }}
        errors={errors}>
        <Controller
          control={control}
          name="recurrenceMonthlyMode"
          render={({ field, fieldState: { error } }) => {
            return (
              <Box>
                <RadioGroup {...field}>
                  <Stack spacing={2}>
                    <Stack
                      sx={{ display: 'flex', alignItems: 'center' }}
                      direction="row"
                      spacing={1}>
                      <FormControlLabel
                        value="DAY_OF_MONTH"
                        control={<Radio size="small" />}
                        label="Day"
                      />
                      <SelectStatic
                        permission={permissions?.recurrenceMonthlyMode1DOM}
                        field={{ name: 'recurrenceMonthlyMode1DOM' }}
                        name="recurrenceMonthlyMode1DOM"
                        disabled={recurrenceMonthlyMode !== 'DAY_OF_MONTH'}
                        customConfig={{ options: dayArray }}
                      />
                      <Box>
                        <FormattedMessage
                          id="OF_THE_MONTH"
                          defaultMessage="of the month"
                        />
                      </Box>
                    </Stack>
                    <Stack
                      sx={{ display: 'flex', alignItems: 'center' }}
                      direction="row"
                      spacing={1}>
                      <FormControlLabel
                        value="ORDINAL_WEEKDAY"
                        control={<Radio size="small" />}
                        label={
                          <FormattedMessage id="THE" defaultMessage="The" />
                        }
                      />
                      <SelectStatic
                        name="recurrenceMonthlyMode2OWO"
                        disabled={recurrenceMonthlyMode !== 'ORDINAL_WEEKDAY'}
                        customConfig={{
                          options: WEEK_OF_MONTH_OPTIONS(formatMessage),
                        }}
                        permission={permissions?.recurrenceMonthlyMode2OWO}
                        field={{ name: 'recurrenceMonthlyMode2OWO' }}
                      />
                      <SelectStatic
                        name="recurrenceMonthlyMode2OWW"
                        disabled={recurrenceMonthlyMode !== 'ORDINAL_WEEKDAY'}
                        customConfig={{ options: WEEKDAYS_LONG }}
                        permission={permissions?.recurrenceMonthlyMode2OWW}
                        field={{ name: 'recurrenceMonthlyMode2OWW' }}
                      />
                      <Box>
                        <FormattedMessage
                          id="OF_THE_MONTH"
                          defaultMessage="of the month"
                        />
                      </Box>
                    </Stack>
                    <Stack
                      sx={{ display: 'flex', alignItems: 'center' }}
                      direction="row"
                      spacing={1}>
                      <FormControlLabel
                        value="ORDINAL_DAY"
                        control={<Radio size="small" />}
                      />
                      <SelectStatic
                        name="recurrenceMonthlyMode3O"
                        disabled={recurrenceMonthlyMode !== 'ORDINAL_DAY'}
                        customConfig={{
                          options:
                            FIRST_LAST_WEEK_OF_MONTH_OPTIONS(formatMessage),
                        }}
                        permission={permissions?.recurrenceMonthlyMode3O}
                        field={{ name: 'recurrenceMonthlyMode3O' }}
                      />
                      <Box>
                        <FormattedMessage
                          id="DAY_OF_THE_MONTH"
                          defaultMessage="day of the month"
                        />
                      </Box>
                    </Stack>
                  </Stack>
                </RadioGroup>
                {error?.message && (
                  <Box sx={{ ml: 2, mt: 1 }}>
                    <FormHelperText error={Boolean(error?.message)}>
                      {error?.message}
                    </FormHelperText>
                  </Box>
                )}
              </Box>
            );
          }}
        />
      </FieldWrapperVertical>
    </Stack>
  );
});

export default RecurrenceOptionsPatternMonthly;
